import { registerPlugin, Capacitor } from "@capacitor/core";
import { captureException } from "../sentry";
import { desktop, isElectron } from "../platform";

/**
 * JS-side wrapper for US-176 Phase 2's device-bound re-auth credential.
 * Two native backends share this one interface:
 *  - iOS: extends KeychainTokenStorePlugin
 *    (mobile/ios/App/App/KeychainTokenStorePlugin.swift, same plugin/jsName
 *    as frontend/src/lib/native/keychainTokenStore.ts, spec §4.3: reuses
 *    AC10's Keychain infrastructure, no second device-secrets mechanism),
 *    reached via Capacitor's registerPlugin.
 *  - Electron desktop: desktop/src/main/reauthCredential.ts, reached via
 *    the existing window.livingsyncs bridge
 *    (desktop/src/preload/index.ts) as `desktop.reauthCredential` — same
 *    method shape, safeStorage-backed instead of Secure-Enclave-backed.
 *
 * Lives here (packages/ui/src/lib/native/, not frontend/) because the real
 * caller — useLogin()'s CUSTOM_AUTH sign-in path — lives in useAuth.ts,
 * same reasoning as this directory's sessionLockTelemetry.ts.
 *
 * `available` MUST be read lazily (a getter, never a value frozen at
 * module-load time): `isElectron`/`desktop` have a well-documented race on
 * exactly this point (see platform.ts's own doc comment on `desktop`) — a
 * `const` snapshot taken before capacitorBridge.ts's refreshDesktopBridge()
 * corrects them would freeze this module's availability wrong for the
 * lifetime of the page, the same bug class already fixed twice elsewhere in
 * this codebase (see the barrel-import/chunk-split gotchas in memory).
 * Capacitor's own check has no such race (window.Capacitor is injected
 * before any page JS runs), but the shared `available` property has to
 * accommodate both backends, so the whole thing is a getter.
 */
interface ReauthCredentialPlugin {
  mintReauthKeypair(): Promise<{ publicKeyPem: string }>;
  signReauthChallenge(options: {
    nonce: string;
  }): Promise<{ signature: string }>;
  hasReauthKeypair(): Promise<{ exists: boolean }>;
  clearReauthKeypair(): Promise<void>;
}

const native = registerPlugin<ReauthCredentialPlugin>("KeychainTokenStore");

const capacitorAvailable = Capacitor.isPluginAvailable("KeychainTokenStore");

function isAvailable(): boolean {
  return (
    capacitorAvailable || (isElectron && !!desktop?.reauthCredential.available)
  );
}

function reportAndRethrow(operation: string, error: unknown): never {
  captureException(error instanceof Error ? error : new Error(String(error)), {
    operation,
    source: "reauthCredential",
  });
  throw error;
}

function assertAvailable(operation: string): void {
  if (!isAvailable()) {
    const err = new Error(
      `reauthCredential.${operation} called but the native re-auth backend is unavailable on this platform`,
    );
    reportAndRethrow(operation, err);
  }
}

export const reauthCredential = {
  get available(): boolean {
    return isAvailable();
  },

  /** Mint a fresh device-bound keypair, replacing any prior one. Returns the
   * PEM/SPKI public key to register via POST /api/me/reauth-credential —
   * the private key never leaves this call. */
  async mint(): Promise<string> {
    assertAvailable("mint");
    try {
      if (isElectron && desktop) {
        const { publicKeyPem } = await desktop.reauthCredential.mint();
        return publicKeyPem;
      }
      const { publicKeyPem } = await native.mintReauthKeypair();
      return publicKeyPem;
    } catch (err) {
      reportAndRethrow("mint", err);
    }
  },

  /** Sign a server-issued nonce (base64url) with the device's private key
   * (Secure Enclave on iOS, safeStorage-encrypted on desktop). Used both
   * for the CUSTOM_AUTH challenge response and for a requireDeviceTrust-
   * gated write's device-assertion header. */
  async sign(nonce: string): Promise<string> {
    assertAvailable("sign");
    try {
      if (isElectron && desktop) {
        const { signature } = await desktop.reauthCredential.sign(nonce);
        return signature;
      }
      const { signature } = await native.signReauthChallenge({ nonce });
      return signature;
    } catch (err) {
      reportAndRethrow("sign", err);
    }
  },

  /** Whether this device already holds a live credential — the lock-screen
   * UI uses this to decide whether to offer the fast-reauth path at all
   * before the employee picks their name. */
  async exists(): Promise<boolean> {
    if (!isAvailable()) return false;
    try {
      if (isElectron && desktop) {
        const { exists } = await desktop.reauthCredential.exists();
        return exists;
      }
      const { exists } = await native.hasReauthKeypair();
      return exists;
    } catch (err) {
      reportAndRethrow("exists", err);
    }
  },

  /** Called from the same onSignOut handler that clears the Phase 0
   * ls.lastLockAt marker on the 8-hour hard ceiling (spec §4.2) — a
   * ceiling sign-out means the next sign-in is cold, not resumed, so the
   * device credential must not survive it. */
  async clear(): Promise<void> {
    if (!isAvailable()) return;
    try {
      if (isElectron && desktop) {
        await desktop.reauthCredential.clear();
        return;
      }
      await native.clearReauthKeypair();
    } catch (err) {
      reportAndRethrow("clear", err);
    }
  },
};
